import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ChevronLeft, ChevronRight, ChevronDown, HandCoins, Clock, CalendarClock } from "lucide-react";
import { format, addWeeks, addMonths, addYears, parseISO } from "date-fns";
import { it } from "date-fns/locale";

const eur = (v) => `€ ${(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const ore = (v) => `${(v || 0).toLocaleString("it-IT", { maximumFractionDigits: 2 })} h`;
const MOVE = { settimana: addWeeks, mese: addMonths, anno: addYears };

function periodLabel(data, periodo) {
  if (!data) return "";
  const s = parseISO(data.inizio), e = parseISO(data.fine);
  if (periodo === "anno") return format(s, "yyyy");
  if (periodo === "mese") return format(s, "MMMM yyyy", { locale: it });
  return `${format(s, "d MMM", { locale: it })} – ${format(e, "d MMM yyyy", { locale: it })}`;
}

const PayToggle = ({ a, onChange }) => (
  <div className="inline-flex rounded-full border bg-slate-50 p-0.5" data-testid={`collab-pay-toggle-${a.id}`}>
    {[[true, "Pagato", "bg-emerald-600 text-white"], [false, "Da pagare", "bg-amber-500 text-white"]].map(([v, l, on]) => (
      <button key={l} type="button" onClick={() => a.collab_pagato !== v && onChange(a, v)}
        data-testid={`collab-pay-${v ? "pagato" : "da-pagare"}-${a.id}`} aria-pressed={a.collab_pagato === v}
        className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${a.collab_pagato === v ? on : "text-slate-500 hover:text-slate-800"}`}>
        {l}
      </button>
    ))}
  </div>
);

const Stat = ({ label, value, cls = "text-slate-800", testid }) => (
  <div><p className="text-[11px] uppercase tracking-wider text-slate-500">{label}</p>
    <p className={`font-mono-data text-base font-semibold ${cls}`} data-testid={testid}>{value}</p></div>
);

function CollabCard({ c, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <Card className="border-slate-200 p-4 sm:p-5" data-testid={`collab-card-${c.id}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full flex-wrap items-center gap-4 text-left" data-testid={`collab-expand-${c.id}`}>
        <div className="flex min-w-[180px] flex-1 items-center gap-3">
          <span className="h-9 w-9 shrink-0 rounded-full" style={{ backgroundColor: `${c.colore}33`, border: `2px solid ${c.colore}` }} />
          <div>
            <p className="font-heading font-semibold text-slate-800">{c.nome} {c.cognome}</p>
            <p className="text-xs text-muted-foreground">{eur(c.costo_orario)}/ora · {c.appuntamenti.length} appuntamenti</p>
          </div>
          <ChevronDown className={`ml-auto h-5 w-5 shrink-0 text-slate-400 transition-transform sm:order-last ${open ? "rotate-180" : ""}`} />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-8">
          <Stat label="Ore lavorate" value={ore(c.ore_lavorate)} testid={`collab-ore-lavorate-${c.id}`} />
          <Stat label="Ore prenotate" value={ore(c.ore_prenotate)} testid={`collab-ore-prenotate-${c.id}`} />
          <Stat label="Da pagare" value={eur(c.da_pagare)} cls="text-amber-600" testid={`collab-da-pagare-${c.id}`} />
          <Stat label="Pagato" value={eur(c.pagato)} cls="text-emerald-600" testid={`collab-pagato-${c.id}`} />
        </div>
      </button>
      {open && (
        <div className="mt-4 space-y-2 border-t pt-4">
          {c.appuntamenti.length === 0 && <p className="text-sm text-muted-foreground">Nessun appuntamento nel periodo.</p>}
          {c.appuntamenti.map((a) => (
            <div key={a.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-100 bg-slate-50/60 p-3" data-testid={`collab-appt-${a.id}`}>
              <div className="min-w-[150px]">
                <p className="text-sm font-medium capitalize text-slate-800">{format(parseISO(a.inizio), "EEEE d MMM", { locale: it })}</p>
                <p className="font-mono-data text-xs text-slate-500">{format(parseISO(a.inizio), "HH:mm")}–{format(parseISO(a.fine), "HH:mm")} · {ore(a.ore)}</p>
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-slate-700">{a.client_nome || "—"}</p>
                <p className="truncate text-xs text-muted-foreground">{a.service_nome}</p>
              </div>
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${a.svolto ? "bg-blue-50 text-blue-700" : "bg-slate-100 text-slate-500"}`}>
                {a.svolto ? "Svolto" : "Prenotato"}
              </span>
              <span className="w-20 text-right font-mono-data text-sm font-semibold text-slate-800">{eur(a.compenso)}</span>
              <PayToggle a={a} onChange={onChange} />
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export default function PagamentoCollaboratori() {
  const [periodo, setPeriodo] = useState("mese");
  const [cursor, setCursor] = useState(new Date());
  const [colFilter, setColFilter] = useState("all");
  const [collaborators, setCollaborators] = useState([]);
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/collaborators").then(({ data }) => setCollaborators(data)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    try {
      const params = { periodo, data: format(cursor, "yyyy-MM-dd") };
      if (colFilter !== "all") params.collaborator_id = colFilter;
      const res = await api.get("/collab-payments", { params });
      setData(res.data);
    } catch { toast.error("Errore caricamento pagamenti collaboratori"); }
  }, [periodo, cursor, colFilter]);
  useEffect(() => { load(); }, [load]);

  const onChange = async (a, pagato) => {
    try {
      await api.put(`/appointments/${a.id}/collab-payment`, { pagato });
      toast.success(pagato ? "Segnato come pagato" : "Segnato come da pagare");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="pagamento-collaboratori-page">
      <PageHeader title="Pagamento Collaboratori" subtitle="Ore lavorate, ore prenotate e compensi da pagare (ore × costo orario)" />

      <div className="flex flex-wrap items-center gap-3">
        <Tabs value={periodo} onValueChange={setPeriodo}>
          <TabsList>
            <TabsTrigger value="settimana" data-testid="periodo-settimana">Settimana</TabsTrigger>
            <TabsTrigger value="mese" data-testid="periodo-mese">Mese</TabsTrigger>
            <TabsTrigger value="anno" data-testid="periodo-anno">Anno</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" onClick={() => setCursor((c) => MOVE[periodo](c, -1))} data-testid="periodo-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <span className="min-w-[150px] text-center text-sm font-semibold capitalize text-slate-800" data-testid="periodo-label">{periodLabel(data, periodo)}</span>
          <Button variant="outline" size="icon" onClick={() => setCursor((c) => MOVE[periodo](c, 1))} data-testid="periodo-next"><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="ghost" size="sm" onClick={() => setCursor(new Date())} data-testid="periodo-oggi">Oggi</Button>
        </div>
        <Select value={colFilter} onValueChange={setColFilter}>
          <SelectTrigger className="w-full sm:w-56" data-testid="collab-filter-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i collaboratori</SelectItem>
            {collaborators.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome} {c.cognome}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {data && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="p-4 border-slate-200"><Stat label="Ore lavorate (periodo)" value={ore(data.collaboratori.reduce((s, c) => s + c.ore_lavorate, 0))} testid="tot-ore-lavorate" /></Card>
          <Card className="p-4 border-amber-200 bg-amber-50/50"><Stat label="Da pagare (periodo)" value={eur(data.totale_da_pagare)} cls="text-amber-600" testid="tot-da-pagare" /></Card>
          <Card className="p-4 border-emerald-200 bg-emerald-50/50"><Stat label="Pagato (periodo)" value={eur(data.totale_pagato)} cls="text-emerald-600" testid="tot-pagato" /></Card>
        </div>
      )}

      {data && data.collaboratori.length === 0 ? (
        <EmptyState icon={HandCoins} title="Nessun collaboratore" description="Aggiungi i collaboratori in Gestione." />
      ) : (
        <div className="space-y-3">
          {data?.collaboratori.map((c) => <CollabCard key={c.id} c={c} onChange={onChange} />)}
        </div>
      )}
      <p className="flex items-center gap-1.5 text-xs text-muted-foreground"><Clock className="h-3.5 w-3.5" /> Solo gli appuntamenti già svolti entrano nel "da pagare"; <CalendarClock className="h-3.5 w-3.5" /> quelli futuri restano come ore prenotate. Gli annullati sono esclusi.</p>
    </div>
  );
}
